import { AppointmentTypes as ApiAppointmentTypes } from 'common';
import { format } from 'date-fns';
import enAU from 'date-fns/locale/en-AU';
import enUS from 'date-fns/locale/en-US';
import { injectable } from 'inversify';

import { AppointmentTypes, IMyBooking } from 'domain/entities/ManageBooking';
import { AsyncHookResult } from 'domain/hooks';
import { IManageBookingRepository } from 'domain/repositories/ManageBookingsRepository';
import { Entity } from 'domain/types';
import mboAPI from 'infrastructure/redux/adapters/mboAPI/mboApi';

const transformAppointment = (
  appointment: ApiAppointmentTypes.ClientAppointmentResponse,
  isUS: boolean,
): Entity<IMyBooking> => ({
  id: String(appointment.id),
  image: appointment.staffImage || '',
  title: appointment.locationName || '',
  rawTime: new Date(appointment.startDateTime),
  bookingDate: format(new Date(appointment.startDateTime), 'EEEE, P', {
    locale: isUS ? enUS : enAU,
  }),
  startTime: format(new Date(appointment.startDateTime), 'hh:mm a'),
  location: appointment.locationName || '',
  instructor: appointment.instructor || '',
  sessionLength: appointment.duration / 60 || 0,
  price: appointment.cost ? appointment.cost : null,
  pointsToEarn: appointment.pointsToEarn,
  priceInPoints: appointment.priceInPoints,
  paidWithPoints: appointment.paidWithPoints,
  bookingIds: appointment.cancelIds || [0],
  type: appointment.appointmentType as AppointmentTypes,
  locationId: appointment.internalLocId,
  hasArrived: appointment.hasArrived,
  checkInAvailableStartDate: new Date(appointment.checkInFromDateTime),
  checkInAvailableStartDateUTC: new Date(appointment.checkInFromDateTimeUTC),
  appointmentName: appointment.appointmentName || '',
});

@injectable()
class ManageBookingRepository implements IManageBookingRepository {
  useListMyBookings(isUS: boolean): AsyncHookResult<Entity<IMyBooking>[]> & {
    query(): void;
  } {
    const [query, result] = mboAPI.useLazyGetClientAppointmentsQuery();

    return {
      query,
      result:
        result?.data
          ?.map((appointment: ApiAppointmentTypes.ClientAppointmentResponse) =>
            transformAppointment(appointment, isUS),
          )
          .sort(
            (a: Entity<IMyBooking>, b: Entity<IMyBooking>) =>
              new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
          ) || [],
      error:
        result.error && !('status' in result.error) && result.error.name !== 'AbortError'
          ? (result.error.message as string | undefined)
          : undefined,
      // Include isFetching so lazy refetches (focus, post-booking, post-cancel)
      // surface a loading state instead of briefly rendering the empty view with
      // stale data. isLoading alone is only true on the first-ever fetch. (GF-1017)
      inProgress: result.isLoading || result.isFetching,
    };
  }
  useListMyUpcomingBookings(isUS: boolean): AsyncHookResult<Entity<IMyBooking>[]> & {
    query(): void;
  } {
    const [query, { data, isLoading, isFetching, error }] =
      mboAPI.useLazyGetUpcomingAppointmentsQuery();

    return {
      query,
      result:
        data
          ?.map((appointment: ApiAppointmentTypes.ClientAppointmentResponse) =>
            transformAppointment(appointment, isUS),
          )
          .sort(
            (a: Entity<IMyBooking>, b: Entity<IMyBooking>) =>
              new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
          ) || [],
      error: error as string | undefined,
      inProgress: isLoading || isFetching,
    };
  }
}

export default ManageBookingRepository;
